import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { dashboardApi, ordensApi, parceirosApi } from '../lib/api';
import { formatCurrency, formatDate, getStatusColor, STATUS_OPTIONS, generateWhatsAppMessageCliente, generateWhatsAppMessageColaborador, copyToClipboard, formatEndereco, formatOrdemNumero } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { 
  Calendar, 
  Clock, 
  DollarSign, 
  Users, 
  UserCog, 
  Wrench,
  Copy,
  MessageCircle,
  Loader2,
  Eye,
  TrendingUp,
  Filter,
  AlertTriangle
} from 'lucide-react';

const spToday = () => new Date().toLocaleDateString('sv-SE', { timeZone: 'America/Sao_Paulo' });

const rangeGeral = (p, ini, fim) => {
  const today = spToday();
  if (p === 'dia') return [today, today];
  if (p === 'semana') {
    const d = new Date(`${today}T12:00:00Z`);
    const dow = (d.getUTCDay() + 6) % 7;
    const mon = new Date(d); mon.setUTCDate(d.getUTCDate() - dow);
    const sun = new Date(mon); sun.setUTCDate(mon.getUTCDate() + 6);
    return [mon.toISOString().slice(0, 10), sun.toISOString().slice(0, 10)];
  }
  if (p === 'mes') {
    const [y, m] = today.split('-');
    const last = new Date(Number(y), Number(m), 0).getDate();
    return [`${y}-${m}-01`, `${y}-${m}-${String(last).padStart(2, '0')}`];
  }
  if (p === 'ano') {
    const y = today.slice(0, 4);
    return [`${y}-01-01`, `${y}-12-31`];
  }
  return [ini || null, fim || null];
};

const Dashboard = () => {
  const [stats, setStats] = useState(null);
  const [ordens, setOrdens] = useState([]);
  const [receitaMensal, setReceitaMensal] = useState([]);
  const [receitaColaborador, setReceitaColaborador] = useState([]);
  const [heatmapOS, setHeatmapOS] = useState([]);
  const [funil, setFunil] = useState(null);
  const [loading, setLoading] = useState(true);
  const [periodoMeses, setPeriodoMeses] = useState(6);
  const [periodoGeral, setPeriodoGeral] = useState('mes');
  const [pgInicio, setPgInicio] = useState('');
  const [pgFim, setPgFim] = useState('');
  const [selectedOrdem, setSelectedOrdem] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const [faturasVencidas, setFaturasVencidas] = useState([]);
  const navigate = useNavigate();

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [gi, gf] = rangeGeral(periodoGeral, pgInicio, pgFim);
      const [statsRes, ordensRes, receitaRes, colabRes, heatRes, funilRes] = await Promise.all([
        dashboardApi.getStats(gi, gf),
        gi && gf ? ordensApi.list('all', null, { data_inicio: gi, data_fim: gf }) : ordensApi.list('all'),
        dashboardApi.getReceitaMensal(periodoMeses, gi, gf),
        dashboardApi.getReceitaPorColaborador(periodoMeses, gi, gf),
        dashboardApi.getHeatmapOS(periodoMeses, gi, gf),
        dashboardApi.getFunilComercial(gi, gf)
      ]);
      setStats(statsRes.data);
      setOrdens(ordensRes.data);
      setReceitaMensal(receitaRes.data);
      setReceitaColaborador(colabRes.data);
      setHeatmapOS(heatRes.data);
      setFunil(funilRes.data);
      parceirosApi.faturasVencidas().then((r) => setFaturasVencidas(r.data)).catch(() => {});
    } catch (error) {
      console.error('Error loading dashboard:', error);
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  }, [periodoMeses, periodoGeral, pgInicio, pgFim]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleCopyWhatsApp = async (ordem, type) => {
    const message = type === 'cliente' 
      ? generateWhatsAppMessageCliente(ordem)
      : generateWhatsAppMessageColaborador(ordem);
    
    const success = await copyToClipboard(message);
    if (success) {
      toast.success(`Mensagem para ${type} copiada!`);
    } else {
      toast.error('Erro ao copiar mensagem');
    }
  };

  const handleUpdateStatus = async (ordemId, newStatus) => {
    try {
      await ordensApi.update(ordemId, { status: newStatus });
      toast.success('Status atualizado!');
      loadData();
    } catch (error) {
      toast.error('Erro ao atualizar status');
    }
  };

  const labelPeriodo = { dia: 'Hoje', semana: 'na Semana', mes: 'no Mês', ano: 'no Ano', custom: 'no Período' }[periodoGeral];
  const labelReceita = { dia: 'Receita de Hoje', semana: 'Receita da Semana', mes: 'Receita do Mês', ano: 'Receita do Ano', custom: 'Receita do Período' }[periodoGeral];
  const tituloPeriodo = { dia: '(hoje)', semana: '(semana atual)', mes: '(mês atual)', ano: '(ano atual)', custom: '(período personalizado)' }[periodoGeral];

  const statCards = [
    { 
      label: `OS ${labelPeriodo}`, 
      value: stats?.os_hoje || 0, 
      icon: Calendar,
      color: '#3B82F6'
    },
    { 
      label: 'Pendentes Pagamento', 
      value: stats?.os_pendentes || 0, 
      icon: Clock,
      color: '#8B5CF6'
    },
    { 
      label: labelReceita, 
      value: formatCurrency(stats?.receita_mes || 0), 
      icon: DollarSign,
      color: '#10B981'
    },
    { 
      label: 'Clientes', 
      value: stats?.total_clientes || 0, 
      icon: Users,
      color: '#0A0A0A'
    },
    { 
      label: 'Colaboradores', 
      value: stats?.total_colaboradores || 0, 
      icon: UserCog,
      color: '#0A0A0A'
    },
    { 
      label: 'Serviços', 
      value: stats?.total_servicos || 0, 
      icon: Wrench,
      color: '#0A0A0A'
    },
  ];

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight" style={{ fontFamily: 'Work Sans' }}>
            Dashboard
          </h1>
          <p className="text-gray-500 mt-1">
            Visão geral do sistema
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2" data-testid="filtro-periodo-geral">
          <div className="flex rounded-lg border border-gray-200 overflow-hidden">
            {[['dia', 'Dia'], ['semana', 'Semana'], ['mes', 'Mês'], ['ano', 'Ano'], ['custom', 'Personalizado']].map(([v, lbl]) => (
              <button
                key={v}
                onClick={() => setPeriodoGeral(v)}
                className={`px-3 py-1.5 text-sm font-medium transition-colors ${periodoGeral === v ? 'bg-emerald-700 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                data-testid={`periodo-geral-${v}`}
              >
                {lbl}
              </button>
            ))}
          </div>
          {periodoGeral === 'custom' && (
            <div className="flex items-center gap-1">
              <input type="date" value={pgInicio} onChange={(e) => setPgInicio(e.target.value)} className="border border-gray-200 rounded-md px-2 py-1 text-sm" data-testid="periodo-geral-inicio" />
              <span className="text-gray-400 text-sm">até</span>
              <input type="date" value={pgFim} onChange={(e) => setPgFim(e.target.value)} className="border border-gray-200 rounded-md px-2 py-1 text-sm" data-testid="periodo-geral-fim" />
            </div>
          )}
        </div>
      </div>

      {/* Alerta de faturas vencidas (parceiros) */}
      {faturasVencidas.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4" data-testid="alerta-faturas-vencidas">
          <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
            <div className="flex items-center gap-2 text-red-700 font-semibold text-sm">
              <AlertTriangle size={17} />
              {faturasVencidas.length} fatura{faturasVencidas.length > 1 ? 's' : ''} de parceiro vencida{faturasVencidas.length > 1 ? 's' : ''} — total {formatCurrency(faturasVencidas.reduce((s, f) => s + f.total, 0))}
            </div>
            <button onClick={() => navigate('/sistema/parceiros')} className="text-xs font-semibold text-red-700 hover:underline" data-testid="ver-faturas-vencidas">
              Ver faturas →
            </button>
          </div>
          <div className="flex flex-wrap gap-2">
            {faturasVencidas.slice(0, 6).map((f) => (
              <button key={f.id} onClick={() => navigate(`/sistema/parceiros/${f.parceiro_id}`)}
                className="flex items-center gap-2 bg-white border border-red-100 rounded-lg px-3 py-1.5 text-xs hover:border-red-300 transition-colors"
                data-testid={`fatura-vencida-${f.codigo}`}>
                <span className="font-semibold text-[#3D4F5F]">{f.codigo}</span>
                <span className="text-gray-500">{f.parceiro_nome}</span>
                <span className="font-semibold text-red-600">{formatCurrency(f.total)}</span>
                <span className="text-red-400">{f.dias_atraso}d em atraso</span>
                {f.pagamento_informado && <span className="text-amber-600 font-medium">💬 pgto. informado</span>}
              </button>
            ))}
            {faturasVencidas.length > 6 && <span className="text-xs text-red-500 self-center">+{faturasVencidas.length - 6} outras</span>}
          </div>
        </div>
      )}

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
        {statCards.map((stat, index) => (
          <Card key={index} className="stat-card" data-testid={`stat-${stat.label.toLowerCase().replace(/\s+/g, '-')}`}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <stat.icon size={24} style={{ color: stat.color }} />
              </div>
              <div className="mt-3">
                <p className="text-2xl font-bold" style={{ fontFamily: 'Work Sans' }}>
                  {loading ? '-' : stat.value}
                </p>
                <p className="text-sm text-gray-500 mt-1">{stat.label}</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Funil Comercial */}
      <Card data-testid="funil-comercial-card">
        <CardHeader>
          <CardTitle style={{ fontFamily: 'Work Sans' }} className="flex items-center gap-2">
            <Filter size={20} className="text-emerald-600" />
            Funil Comercial
            {funil && (
              <span className="ml-2 text-sm font-normal text-gray-500">
                · {funil.total} lead{funil.total !== 1 ? 's' : ''} no período
                {funil.total > 0 && (
                  <span className="text-gray-400"> ({funil.manuais ?? funil.total} cadastrados pela equipe · {funil.automaticos ?? 0} automáticos)</span>
                )}
              </span>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading || !funil ? (
            <div className="flex justify-center py-8"><Loader2 className="w-8 h-8 animate-spin text-gray-400" /></div>
          ) : funil.total === 0 ? (
            <div className="text-center py-6 text-gray-500">Nenhum lead no período selecionado.</div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2 space-y-2">
                {funil.itens.map((it) => {
                  const color = {
                    'Novo Lead': ['#3B82F6', 'bg-blue-500'],
                    'Em Atendimento': ['#8B5CF6', 'bg-violet-500'],
                    'Aguardando Informações': ['#06B6D4', 'bg-cyan-500'],
                    'Orçamento Enviado': ['#F59E0B', 'bg-amber-500'],
                    'Follow-up': ['#EAB308', 'bg-yellow-500'],
                    'Aprovado': ['#10B981', 'bg-emerald-500'],
                    'Perdido': ['#EF4444', 'bg-red-500']
                  }[it.status] || ['#6B7280', 'bg-gray-500'];
                  const width = funil.total ? (it.quantidade / funil.total) * 100 : 0;
                  return (
                    <div key={it.status} className="flex items-center gap-3" data-testid={`funil-row-${it.status}`}>
                      <div className="w-40 text-sm text-gray-700 truncate" title={it.status}>{it.status}</div>
                      <div className="flex-1 h-6 bg-gray-100 rounded-md overflow-hidden relative">
                        <div className={`h-full ${color[1]} transition-all`} style={{ width: `${width}%` }} />
                      </div>
                      <div className="w-28 text-right text-xs text-gray-700 tabular-nums font-medium">
                        {it.quantidade} · {it.percentual}%
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="grid grid-cols-2 gap-3 lg:grid-cols-1">
                <div className="rounded-lg bg-emerald-50 border border-emerald-100 p-3" data-testid="funil-metric-conversao">
                  <p className="text-xs text-emerald-700 font-medium">Conversão geral</p>
                  <p className="text-2xl font-bold text-emerald-900 tabular-nums">
                    {funil.metricas.conversao_geral_percentual}%
                  </p>
                  <p className="text-[10px] text-emerald-800/70">{funil.metricas.aprovados}/{funil.total} leads aprovados</p>
                </div>
                <div className="rounded-lg bg-amber-50 border border-amber-100 p-3" data-testid="funil-metric-pos-orc">
                  <p className="text-xs text-amber-700 font-medium">Pós-orçamento</p>
                  <p className="text-2xl font-bold text-amber-900 tabular-nums">
                    {funil.metricas.conversao_pos_orcamento_percentual}%
                  </p>
                  <p className="text-[10px] text-amber-800/70">após envio do orçamento</p>
                </div>
                <div className="rounded-lg bg-red-50 border border-red-100 p-3 col-span-2 lg:col-span-1" data-testid="funil-metric-perda">
                  <p className="text-xs text-red-700 font-medium">Taxa de perda</p>
                  <p className="text-2xl font-bold text-red-900 tabular-nums">
                    {funil.metricas.taxa_perda_percentual}%
                  </p>
                  <p className="text-[10px] text-red-800/70">{funil.metricas.perdidos} leads marcados como Perdido</p>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Receita Mensal Chart */}
      <Card data-testid="receita-mensal-card">
        <CardHeader className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <CardTitle style={{ fontFamily: 'Work Sans' }} className="flex items-center gap-2">
            <TrendingUp size={20} className="text-emerald-600" />
            Receita {tituloPeriodo}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
            </div>
          ) : receitaMensal.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              Sem dados financeiros para exibir
            </div>
          ) : (
            <div className="w-full h-72" data-testid="receita-mensal-chart">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={receitaMensal} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                  <XAxis dataKey="mes" tick={{ fontSize: 12, fill: '#6B7280' }} />
                  <YAxis 
                    tick={{ fontSize: 12, fill: '#6B7280' }}
                    tickFormatter={(v) => v >= 1000 ? `R$ ${(v/1000).toFixed(1)}k` : `R$ ${v}`}
                  />
                  <Tooltip 
                    formatter={(value) => formatCurrency(value)}
                    contentStyle={{ borderRadius: 8, border: '1px solid #E5E7EB' }}
                  />
                  <Legend wrapperStyle={{ fontSize: 13 }} />
                  <Bar dataKey="entrada" name="Entradas" fill="#10B981" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="saida" name="Saídas" fill="#EF4444" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Receita por Colaborador + Heatmap OS */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card data-testid="receita-colaborador-card">
          <CardHeader>
            <CardTitle style={{ fontFamily: 'Work Sans' }} className="flex items-center gap-2">
              <UserCog size={20} className="text-blue-600" />
              Top Colaboradores {tituloPeriodo}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex justify-center py-8">
                <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
              </div>
            ) : receitaColaborador.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                Nenhuma OS concluída no período
              </div>
            ) : (
              <div className="space-y-3" data-testid="receita-colaborador-list">
                {receitaColaborador.slice(0, 6).map((c, idx) => {
                  const max = receitaColaborador[0]?.total || 1;
                  const pct = max > 0 ? (c.total / max) * 100 : 0;
                  return (
                    <div key={c.colaborador} data-testid={`colab-row-${idx}`}>
                      <div className="flex justify-between text-sm mb-1">
                        <span className="font-medium truncate max-w-[60%]">{c.colaborador}</span>
                        <span className="text-gray-600 tabular-nums">
                          {formatCurrency(c.total)} <span className="text-gray-400">({c.qtd_os} OS)</span>
                        </span>
                      </div>
                      <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-gradient-to-r from-blue-500 to-emerald-500 rounded-full transition-all"
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        <Card data-testid="heatmap-os-card">
          <CardHeader>
            <CardTitle style={{ fontFamily: 'Work Sans' }} className="flex items-center gap-2">
              <Calendar size={20} className="text-purple-600" />
              OS por Dia da Semana {tituloPeriodo}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex justify-center py-8">
                <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
              </div>
            ) : heatmapOS.every((d) => d.qtd === 0) ? (
              <div className="text-center py-8 text-gray-500">
                Nenhuma OS no período
              </div>
            ) : (
              <div className="grid grid-cols-7 gap-2" data-testid="heatmap-grid">
                {heatmapOS.map((d) => {
                  const max = Math.max(...heatmapOS.map((x) => x.qtd), 1);
                  const intensity = d.qtd / max;
                  const bg = d.qtd === 0
                    ? '#F3F4F6'
                    : `rgba(139, 92, 246, ${0.2 + intensity * 0.8})`;
                  const textColor = intensity > 0.5 ? 'white' : '#1F2937';
                  return (
                    <div key={d.dia} className="flex flex-col items-center gap-1" data-testid={`heat-cell-${d.dia}`}>
                      <div
                        className="w-full aspect-square rounded-lg flex items-center justify-center font-bold text-lg"
                        style={{ backgroundColor: bg, color: textColor }}
                      >
                        {d.qtd}
                      </div>
                      <span className="text-xs text-gray-500">{d.dia.slice(0, 3)}</span>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Ordens de Serviço */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle style={{ fontFamily: 'Work Sans' }}>Ordens de Serviço {tituloPeriodo}</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
                </div>
              ) : ordens.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  Nenhuma ordem de serviço encontrada
                </div>
              ) : (
                <div className="table-fit">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Nº OS</th>
                        <th>Data</th>
                        <th className="hidden lg:table-cell">Hora</th>
                        <th>Cliente</th>
                        <th className="hidden lg:table-cell">Serviço</th>
                        <th className="hidden xl:table-cell">Colaborador</th>
                        <th>Valor</th>
                        <th>Status</th>
                        <th className="text-right">Ações</th>
                      </tr>
                    </thead>
                    <tbody>
                      {ordens.map((ordem) => (
                        <tr key={ordem.id} data-testid={`ordem-row-${ordem.id}`}>
                          <td className="font-mono text-xs">
                            {ordem.numero ? (
                              <span className="px-2 py-1 rounded bg-gray-100 border font-semibold text-gray-700">
                                {formatOrdemNumero(ordem.numero)}
                              </span>
                            ) : <span className="text-gray-400">—</span>}
                          </td>
                          <td>{formatDate(ordem.data_agendamento)}</td>
                          <td className="hidden lg:table-cell">{ordem.hora_agendamento}</td>
                          <td className="font-medium">{ordem.cliente_nome}</td>
                          <td className="cell-ellipsis hidden lg:table-cell" title={ordem.servico_nome}>{ordem.servico_nome}</td>
                          <td className="hidden xl:table-cell">{ordem.colaborador_nome}</td>
                          <td>{formatCurrency(ordem.valor)}</td>
                          <td>
                            <Badge 
                              style={{ backgroundColor: getStatusColor(ordem.status), color: 'white' }}
                            >
                              {ordem.status}
                            </Badge>
                          </td>
                          <td>
                            <div className="flex items-center gap-2 flex-nowrap justify-end">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                  setSelectedOrdem(ordem);
                                  setShowDetails(true);
                                }}
                                data-testid={`view-ordem-${ordem.id}`}
                              >
                                <Eye size={16} />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleCopyWhatsApp(ordem, 'cliente')}
                                title="Copiar mensagem para cliente"
                                data-testid={`copy-cliente-${ordem.id}`}
                              >
                                <MessageCircle size={16} className="text-green-600" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
        </CardContent>
      </Card>

      {/* Order Details Dialog */}
      <Dialog open={showDetails} onOpenChange={setShowDetails}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Work Sans' }}>
              Detalhes da Ordem de Serviço
              {selectedOrdem?.numero && (
                <span className="ml-2 text-sm font-mono px-2 py-0.5 rounded bg-gray-100 border font-semibold text-gray-700 align-middle">
                  {formatOrdemNumero(selectedOrdem.numero)}
                </span>
              )}
            </DialogTitle>
            <DialogDescription>
              Visualize e gerencie os detalhes desta ordem
            </DialogDescription>
          </DialogHeader>
          
          {selectedOrdem && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-500">Cliente</p>
                  <p className="font-medium">{selectedOrdem.cliente_nome}</p>
                  <p className="text-sm text-gray-500">{selectedOrdem.cliente_telefone}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Colaborador</p>
                  <p className="font-medium">{selectedOrdem.colaborador_nome}</p>
                  <p className="text-sm text-gray-500">{selectedOrdem.colaborador_telefone}</p>
                </div>
              </div>

              <div>
                <p className="text-sm text-gray-500">Endereço</p>
                <p className="font-medium">{formatEndereco(selectedOrdem.cliente_endereco)}</p>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <p className="text-sm text-gray-500">Serviço</p>
                  <p className="font-medium">{selectedOrdem.servico_nome}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Data/Hora</p>
                  <p className="font-medium">{formatDate(selectedOrdem.data_agendamento)} - {selectedOrdem.hora_agendamento}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Valor</p>
                  <p className="font-medium">{formatCurrency(selectedOrdem.valor)}</p>
                </div>
              </div>

              {selectedOrdem.observacoes && (
                <div>
                  <p className="text-sm text-gray-500">Observações</p>
                  <p className="font-medium">{selectedOrdem.observacoes}</p>
                </div>
              )}

              <div>
                <p className="text-sm text-gray-500 mb-2">Status</p>
                <Select 
                  value={selectedOrdem.status} 
                  onValueChange={(value) => handleUpdateStatus(selectedOrdem.id, value)}
                >
                  <SelectTrigger data-testid="status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {STATUS_OPTIONS.map((status) => (
                      <SelectItem key={status} value={status}>
                        {status}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex gap-2 pt-4 border-t">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => handleCopyWhatsApp(selectedOrdem, 'cliente')}
                  data-testid="copy-whatsapp-cliente"
                >
                  <Copy size={16} className="mr-2" />
                  Copiar p/ Cliente
                </Button>
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => handleCopyWhatsApp(selectedOrdem, 'colaborador')}
                  data-testid="copy-whatsapp-colaborador"
                >
                  <Copy size={16} className="mr-2" />
                  Copiar p/ Colaborador
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Dashboard;
